import React from 'react';
import { useTranslation } from 'react-i18next';
import { PencilIcon, PlusIcon, TrashIcon } from '@hero/web-icons';
import { Button } from '../../../ui/Button';
import { usePricingSheetStore } from './store/usePricingSheetStore';

/**
 * Sticky toolbar shown above the table when one or more rows are checked.
 * Subscribes to {@link usePricingSheetStore} for selection and bulk actions
 * so callers don't have to wire callbacks down from the page.
 *
 * Returns `null` when nothing is selected — the page can render the
 * component unconditionally without leaking layout space.
 */
export function PricingSheetBulkBar() {
  const { t } = useTranslation(['m-pricing', 'c-main']);

  const selectedCount = usePricingSheetStore(s => s.selectedIds.length);
  const bulkDuplicate = usePricingSheetStore(s => s.bulkDuplicate);
  const bulkDelete = usePricingSheetStore(s => s.bulkDelete);
  const clearSelection = usePricingSheetStore(s => s.clearSelection);

  if (selectedCount === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2">
      <div className="flex items-center gap-2 pe-2">
        <span className="rounded-full bg-primary px-2 py-0.5 text-[11px] font-semibold text-white">
          {selectedCount}
        </span>
        <span className="text-xs font-semibold text-primary">
          {t('sheet.bulk.selected', {
            defaultValue: '{{count}} selected',
            count: selectedCount,
          })}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 gap-1 bg-white text-xs"
          onClick={bulkDuplicate}>
          <PlusIcon className="size-3" />
          {t('sheet.bulk.duplicate', { defaultValue: 'Duplicate' })}
        </Button>

        {/* Apply rate / Set field are wired in a follow-up — the store hook
            exposes `changeRow` so building these is just a UI pass. */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 gap-1 bg-white text-xs"
          disabled
          onClick={() => undefined}>
          <PencilIcon className="size-3" />
          {t('sheet.bulk.apply_rate', { defaultValue: 'Apply rate' })}
        </Button>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 gap-1 bg-white text-xs"
          disabled
          onClick={() => undefined}>
          <PencilIcon className="size-3" />
          {t('sheet.bulk.apply_field', { defaultValue: 'Set field' })}
        </Button>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 gap-1 bg-white text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
          onClick={bulkDelete}>
          <TrashIcon className="size-3" />
          {t('sheet.bulk.delete', { defaultValue: 'Delete' })}
        </Button>
      </div>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="ms-auto h-7 text-xs text-gray-500"
        onClick={clearSelection}>
        {t('c-main:clear', { defaultValue: 'Clear' })}
      </Button>
    </div>
  );
}
